import React, { useCallback, useEffect, useState } from "react"
import { Button, Card, CardBody, Col, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row, Spinner, Table } from "reactstrap"
import { createSalesUser, getSalesAccounts, logoutRoleSessions, updateSalesUser } from "helpers/backendHelpers/adminAccess"

const EMPTY_FORM = { userName: "", mobileNo: "", password: "" }

const AddSalesUserCard = ({ onChange }) => {
  const [form, setForm] = useState(EMPTY_FORM)
  const [loading, setLoading] = useState(false)

  const setField = field => e => setForm(prev => ({ ...prev, [field]: e.target.value }))
  const canSubmit = form.userName.trim() && /^\d{10}$/.test(form.mobileNo) && form.password

  const submit = async () => {
    setLoading(true)
    try {
      await createSalesUser({ ...form, userName: form.userName.trim() })
      setForm(EMPTY_FORM)
      onChange()
    } catch (_) {
      // api_helper already shows the error toast
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="rounded border bg-light p-3">
        <h6 className="fw-bold mb-3">Add Sales User</h6>
        <Row className="align-items-end">
          <Col md={3} className="mb-3">
            <Label>User Name</Label>
            <Input value={form.userName} onChange={setField("userName")} />
          </Col>
          <Col md={3} className="mb-3">
            <Label>Mobile No.</Label>
            <Input value={form.mobileNo} maxLength={10} onChange={e => setForm(prev => ({ ...prev, mobileNo: e.target.value.replace(/\D/g, "") }))} />
          </Col>
          <Col md={3} className="mb-3">
            <Label>Password</Label>
            <Input type="password" autoComplete="new-password" value={form.password} onChange={setField("password")} />
          </Col>
          <Col md={3} className="mb-3">
            <Button color="primary" type="button" disabled={loading || !canSubmit} onClick={submit}>
              {loading ? <Spinner size="sm" /> : "Add Sales User"}
            </Button>
          </Col>
        </Row>
    </div>
  )
}

export const StatusSwitch = ({ checked, onChange, disabled }) => {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => !disabled && onChange(!checked)}
      onKeyDown={e => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault()
          !disabled && onChange(!checked)
        }
      }}
      style={{
        display: "inline-flex",
        alignItems: "center",
        cursor: disabled ? "not-allowed" : "pointer",
        userSelect: "none",
        gap: "8px",
      }}
    >
      <div
        style={{
          width: "42px",
          height: "22px",
          borderRadius: "22px",
          backgroundColor: checked ? "#34c38f" : "#adb5bd",
          position: "relative",
          transition: "background-color 0.2s ease",
          boxShadow: "inset 0 1px 2px rgba(0,0,0,0.15)",
        }}
      >
        <div
          style={{
            width: "16px",
            height: "16px",
            borderRadius: "50%",
            backgroundColor: "#fff",
            position: "absolute",
            top: "3px",
            left: checked ? "23px" : "3px",
            transition: "left 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            boxShadow: "0 2px 4px rgba(0,0,0,0.25)",
          }}
        />
      </div>
      <span
        style={{
          fontSize: "13px",
          fontWeight: 600,
          color: checked ? "#34c38f" : "#74788d",
        }}
      >
        {checked ? "Active" : "Inactive"}
      </span>
    </div>
  )
}

const SalesUserListCard = ({ refreshKey, onChange }) => {
  const [accounts, setAccounts] = useState([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState(null)
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    try {
      const response = await getSalesAccounts()
      setAccounts(response?.payload?.accounts || [])
    } catch (_) {
      // api_helper already shows the error toast
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load, refreshKey])

  const canSave = editing && editing.userName.trim() && /^\d{10}$/.test(editing.mobileNo)

  const save = async () => {
    setSaving(true)
    try {
      await updateSalesUser(editing._id, { userName: editing.userName.trim(), mobileNo: editing.mobileNo, password: editing.password })
      setEditing(null)
      onChange()
    } catch (_) {
      // api_helper already shows the error toast
    } finally {
      setSaving(false)
    }
  }

  const handleToggleStatus = async (account, newStatus) => {
    setAccounts(prev => prev.map(a => a._id === account._id ? { ...a, isActive: newStatus } : a))
    try {
      await updateSalesUser(account._id, { isActive: newStatus })
      onChange()
    } catch (_) {
      // Revert if API fails
      setAccounts(prev => prev.map(a => a._id === account._id ? { ...a, isActive: !newStatus } : a))
    }
  }

  return (
    <div>
        <h6 className="fw-bold mb-3">Sales Users ({accounts.length})</h6>
        {loading ? (
          <div className="text-center py-4"><Spinner color="primary" /></div>
        ) : accounts.length ? (
          <div className="table-responsive">
            <Table className="align-middle mb-0">
              <thead>
                <tr>
                  <th>#</th>
                  <th>User Name</th>
                  <th>Mobile No.</th>
                  <th>Status</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {accounts.map((account, index) => (
                  <tr key={account._id}>
                    <td>{index + 1}</td>
                    <td>{account.userName}</td>
                    <td>{account.mobileNo}</td>
                    <td>
                      <StatusSwitch
                        checked={Boolean(account.isActive)}
                        onChange={newStatus => handleToggleStatus(account, newStatus)}
                      />
                    </td>
                    <td className="text-end">
                      <Button color="primary" outline size="sm" type="button" onClick={() => setEditing({ ...account, password: "" })}>
                        <i className="bx bx-edit-alt me-1"></i>Edit
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        ) : (
          <div className="text-muted">No sales users yet.</div>
        )}

      <Modal isOpen={!!editing} toggle={() => setEditing(null)} centered>
        <ModalHeader toggle={() => setEditing(null)}>Edit Sales User</ModalHeader>
        {editing && (
          <ModalBody>
            <div className="mb-3">
              <Label>User Name</Label>
              <Input value={editing.userName} onChange={e => setEditing(prev => ({ ...prev, userName: e.target.value }))} />
            </div>
            <div className="mb-3">
              <Label>Mobile No.</Label>
              <Input value={editing.mobileNo} maxLength={10} onChange={e => setEditing(prev => ({ ...prev, mobileNo: e.target.value.replace(/\D/g, "") }))} />
            </div>
            <div>
              <Label>New Password</Label>
              <Input type="password" autoComplete="new-password" placeholder="Leave blank to keep current" value={editing.password} onChange={e => setEditing(prev => ({ ...prev, password: e.target.value }))} />
            </div>
          </ModalBody>
        )}
        <ModalFooter>
          <Button color="secondary" type="button" onClick={() => setEditing(null)}>Cancel</Button>
          <Button color="primary" type="button" disabled={saving || !canSave} onClick={save}>
            {saving ? <Spinner size="sm" /> : "Save"}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}

const SessionLogoutCard = () => {
  const [loadingRole, setLoadingRole] = useState(null)

  const logoutAll = async role => {
    const note = role === "admin" ? " This includes your own session." : ""
    if (!window.confirm(`Log out all ${role} accounts from every device?${note}`)) return
    setLoadingRole(role)
    try {
      await logoutRoleSessions(role)
      if (role === "admin") {
        localStorage.clear()
        window.location.href = "/login"
      }
    } catch (_) {
      // api_helper already shows the error toast
    } finally {
      setLoadingRole(null)
    }
  }

  return (
    <Card>
      <CardBody className="d-flex justify-content-between align-items-center flex-wrap gap-3">
        <div>
          <h5 className="font-size-16 fw-bold mb-1">Session Logout</h5>
          <p className="text-muted font-size-13 mb-0">Force every logged-in device of the selected accounts to sign in again.</p>
        </div>
        <div className="d-flex gap-2 flex-wrap">
          <Button color="danger" outline type="button" disabled={!!loadingRole} onClick={() => logoutAll("sales")}>
            {loadingRole === "sales" ? <Spinner size="sm" /> : "Logout All Sales Accounts"}
          </Button>
          <Button color="danger" type="button" disabled={!!loadingRole} onClick={() => logoutAll("admin")}>
            {loadingRole === "admin" ? <Spinner size="sm" /> : "Logout All Admin Accounts"}
          </Button>
        </div>
      </CardBody>
    </Card>
  )
}

export const SalesUsersSection = () => {
  const [refreshKey, setRefreshKey] = useState(0)
  const refresh = () => setRefreshKey(key => key + 1)

  return (
    <Card>
      <CardBody>
        <div className="mb-3">
          <h5 className="font-size-16 fw-bold mb-1">Sales Users</h5>
          <p className="text-muted font-size-13 mb-0">Create sales accounts and control who can sign in.</p>
        </div>
        <AddSalesUserCard onChange={refresh} />
        <hr className="my-4" />
        <SalesUserListCard refreshKey={refreshKey} onChange={refresh} />
      </CardBody>
    </Card>
  )
}

export { SessionLogoutCard }
